/**
 * Telling somewhere else that something happened here.
 *
 * A webhook belongs to a **table**, not to a view, and this is the only way to
 * make one: there is no MCP tool for webhooks, so if the interface cannot do it
 * nothing can.
 *
 * The list is the cache entry and every mutation patches it, for the reason the
 * views list is patched: each answer says exactly what changed. Unlike a view,
 * nothing broadcasts a webhook -- there is no realtime event for one -- so the
 * patch is not a shortcut past the socket, it is the only thing that keeps two
 * tabs from disagreeing until one of them reloads.
 */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { TableWebhook, WebhookEvent, WebhookTestCall } from '@/api/types'

/** What the form holds, which is what both writes send. */
/** The five methods the endpoint accepts, narrowed from the optional field. */
export type WebhookMethod = NonNullable<TableWebhook['request_method']>

export interface WebhookDraft {
  name: string
  url: string
  request_method: WebhookMethod
  include_all_events: boolean
  events: WebhookEvent[]
  headers: Record<string, string>
  use_user_field_names: boolean
  active: boolean
}

export function webhooksQuery(tableId: number) {
  return queryOptions({
    queryKey: qk.webhooks(tableId),
    queryFn: (): Promise<TableWebhook[]> =>
      unwrap(
        getApi().GET('/api/database/webhooks/table/{table_id}/', {
          params: { path: { table_id: tableId } },
        }),
      ),
  })
}

export function useWebhooks(tableId: number) {
  return useQuery(webhooksQuery(tableId))
}

export interface WebhookMutations {
  create: (draft: WebhookDraft, onDone: () => void) => void
  update: (webhookId: number, draft: WebhookDraft) => void
  remove: (webhookId: number) => void
  isPending: boolean
}

/**
 * The body both writes send.
 *
 * `events` is sent even when every event is wanted, because the two flags are
 * not alternatives to the backend: `include_all_events` decides what fires and
 * the list is what it falls back to when that is switched off. Dropping the
 * list would empty it, so turning "all events" off later would leave a webhook
 * listening for nothing.
 */
const bodyFor = (draft: WebhookDraft) => ({
  name: draft.name.trim(),
  url: draft.url.trim(),
  request_method: draft.request_method,
  include_all_events: draft.include_all_events,
  events: draft.events,
  headers: draft.headers,
  use_user_field_names: draft.use_user_field_names,
  active: draft.active,
})

export function useWebhookMutations(tableId: number): WebhookMutations {
  const queryClient = useQueryClient()
  const key = qk.webhooks(tableId)

  const store = (webhook: TableWebhook): void => {
    queryClient.setQueryData<TableWebhook[]>(key, (held) => {
      if (held === undefined) return held
      const known = held.some((one) => one.id === webhook.id)
      return known
        ? held.map((one) => (one.id === webhook.id ? webhook : one))
        : [...held, webhook]
    })
  }

  const create = useMutation({
    mutationFn: (draft: WebhookDraft): Promise<TableWebhook> =>
      unwrap(
        getApi().POST('/api/database/webhooks/table/{table_id}/', {
          params: { path: { table_id: tableId } },
          body: bodyFor(draft),
        }),
      ),
    onSuccess: store,
  })

  const update = useMutation({
    mutationFn: ({
      webhookId,
      draft,
    }: {
      webhookId: number
      draft: WebhookDraft
    }): Promise<TableWebhook> =>
      unwrap(
        getApi().PATCH('/api/database/webhooks/{webhook_id}/', {
          params: { path: { webhook_id: webhookId } },
          body: bodyFor(draft),
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (webhookId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/webhooks/{webhook_id}/', {
          params: { path: { webhook_id: webhookId } },
        }),
      ),
    onSuccess: (_answer, webhookId) => {
      queryClient.setQueryData<TableWebhook[]>(key, (held) =>
        held === undefined ? held : held.filter((one) => one.id !== webhookId),
      )
    },
  })

  return {
    create: (draft, onDone) => {
      create.mutate(draft, { onSuccess: onDone })
    },
    update: (webhookId, draft) => {
      update.mutate({ webhookId, draft })
    },
    remove: (webhookId) => {
      remove.mutate(webhookId)
    },
    isPending: create.isPending || update.isPending || remove.isPending,
  }
}

/**
 * Sends one made-up call, so a webhook can be checked before it is saved.
 *
 * Deliberately not a query: it has a side effect somewhere else, and asking
 * twice is asking the other end twice. The answer is the request and response
 * as text, which is the whole point -- a webhook that does not work fails
 * silently by design.
 */
export function useTestWebhook(tableId: number) {
  return useMutation({
    mutationFn: ({
      url,
      eventType,
    }: {
      url: string
      eventType: WebhookEvent
    }): Promise<WebhookTestCall> =>
      unwrap(
        getApi().POST('/api/database/webhooks/table/{table_id}/test-call/', {
          params: { path: { table_id: tableId } },
          body: { url: url.trim(), event_type: eventType },
        }),
      ),
  })
}
